/**
 * Inbound lanes (ADR-0024, ENG-6407 Slice 1).
 *
 * Every inbound multiplexes into one Claude Code session transcript as an
 * undifferentiated `<channel ...>` user turn. The delivery-protection stack
 * (pending-inbound markers, the ghost-reply Stop hook, busy-ack, durable
 * replay) then RECONSTRUCTS, after the fact, the one distinction it actually
 * needs: does the agent owe a human a reply on a channel surface this turn?
 *
 * This module records that distinction ONCE, at the source, as two
 * first-class attributes the producer stamps onto the `<channel>` tag:
 *
 *   - `lane`         - provenance, knowable and stable at injection.
 *   - `expects_reply`- a turn-time reply-obligation prediction, defaulted from
 *                      `lane` but explicitly overridable by the producer.
 *
 * Slice 1 is PURE ADDITIVE PLUMBING: producers emit the attributes and they are
 * recorded only. No consumer changes behaviour on them yet (gating is Slice 2).
 * Consumers therefore default a missing/unknown lane to `conversational` (the
 * conservative fail-direction), so an old producer that emits neither attribute
 * degrades to today's behaviour, never to silent loss.
 *
 * This is the canonical source of truth. `packages/mcp` keeps a tiny
 * self-contained mirror (`inbound-lanes-runtime.ts`) because the channel-server
 * bundle deliberately carries no `@augmented/core` dependency; the closed
 * directive allowlist lives ONLY here and is consumed directly by the manager
 * (apps/cli depends on core).
 */

/** The three inbound provenance lanes (ADR-0024 Decision 2). */
export const INBOUND_LANES = ['conversational', 'directive', 'liveness'] as const;
export type InboundLane = (typeof INBOUND_LANES)[number];

/**
 * Closed `directive` allowlist (ADR-0024 Decision 6). These are system
 * injections whose contract is "go do work", not "reply to a human".
 *
 * The list is deliberately CLOSED: everything not in it (and not `liveness`) is
 * `conversational`. That makes "default to conversational when unsure" a
 * structural guarantee rather than a cultural one, so the surrounding cost /
 * noise-reduction work can never erode the fail-safe by quietly reclassifying a
 * conversational turn as directive. Adding a kind is an explicit, reviewable
 * list-edit; the guard test (`__tests__/inbound-lanes.test.ts`) asserts the set
 * never grows by accident.
 *
 * Note: `liveness` ("are you online?"-class probes) is NOT a directive kind. It
 * is its own lane, intercepted by the manager-side responder, and never routed
 * through directive handling.
 */
export const DIRECTIVE_KINDS = [
  'scheduled-task',
  'kanban',
  'loop',
  'hot-reload',
  'kickoff',
] as const;
export type DirectiveKind = (typeof DIRECTIVE_KINDS)[number];

const DIRECTIVE_SET: ReadonlySet<string> = new Set(DIRECTIVE_KINDS);

/** True if `kind` is a recognised directive (go-do-work) injection kind. */
export function isDirectiveKind(kind: string): kind is DirectiveKind {
  return DIRECTIVE_SET.has(kind);
}

/**
 * Classify a producer-supplied injection `kind` into its lane. A directive-kind
 * string folds to `directive`; everything else folds to `conversational` (the
 * conservative default). `liveness` is not produced here - it is set explicitly
 * by the manager-side liveness path, never inferred from a kind string.
 */
export function classifyLane(kind: string): InboundLane {
  return isDirectiveKind(kind) ? 'directive' : 'conversational';
}

/**
 * The reply-obligation a lane DEFAULTS to before any producer override:
 * conversational -> true (a human is owed a reply), directive -> false
 * (execution + status is the contract), liveness -> false (the manager, not the
 * model, answers the probe).
 */
export function defaultExpectsReply(lane: InboundLane): boolean {
  return lane === 'conversational';
}

/**
 * Parse a raw `lane=` attribute value (from the `<channel>` tag) into a lane,
 * defaulting a missing/unknown value to `conversational` (ADR-0024 fail-safe).
 * This is the consumer-side default that lets Slice 1 ship with zero gating: an
 * untagged or future-unknown lane behaves exactly like today.
 */
export function parseLane(raw: string | null | undefined): InboundLane {
  return raw === 'directive' || raw === 'liveness' ? raw : 'conversational';
}

/**
 * Parse a raw `expects_reply=` attribute value. Only the explicit strings
 * `'true'` / `'false'` override; anything missing or unrecognised inherits the
 * `lane` prior (ADR-0024 AC3).
 */
export function parseExpectsReply(
  raw: string | null | undefined,
  lane: InboundLane,
): boolean {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return defaultExpectsReply(lane);
}

/**
 * Build the `{ lane, expects_reply }` STRING fragment a producer merges into the
 * `notifications/claude/channel` `meta` object. Claude Code renders each meta
 * key as a `<channel ...>` tag attribute and its Zod schema rejects non-string
 * values, so both values are strings. `expectsReply` is optional - omit it to
 * take the lane default.
 */
export function laneMetaAttrs(
  lane: InboundLane,
  expectsReply?: boolean,
): { lane: string; expects_reply: string } {
  const er = expectsReply ?? defaultExpectsReply(lane);
  return { lane, expects_reply: er ? 'true' : 'false' };
}

/**
 * Render the same two attributes as an inline XML fragment (`lane="x"
 * expects_reply="y"`) for the one producer that builds the `<channel>` tag as a
 * literal string rather than via meta - the manager's direct-chat tmux
 * injection. The values are a closed enum / boolean, so no escaping is needed.
 */
export function laneTagFragment(lane: InboundLane, expectsReply?: boolean): string {
  const attrs = laneMetaAttrs(lane, expectsReply);
  return `lane="${attrs.lane}" expects_reply="${attrs.expects_reply}"`;
}
